import { useNotification } from "hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Invitation, InvitationDTO } from "types/invitation.type";
import instance from "./axiosInstance";
import { CustomerQueryKey } from "./customer.api";

export const ENDPOINT = "invitation";
export const GET_INVITATIONS_KEY = "get-invitations";

export const useGetInvitations = () =>
  useQuery({
    queryKey: [GET_INVITATIONS_KEY],
    queryFn: async (): Promise<Invitation[]> => {
      const response = await instance.get(ENDPOINT);
      return response.data;
    },
  });

export const useGetInvitation = (code: string) =>
  useQuery({
    queryKey: [GET_INVITATIONS_KEY],
    queryFn: async (): Promise<Invitation> => {
      const response = await instance.get(`${ENDPOINT}/${code}`);
      return response.data;
    },
  });

export const useInviteUser = () => {
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InvitationDTO): Promise<Invitation> => {
      return instance.post(ENDPOINT, { ...payload, role: +payload.role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_INVITATIONS_KEY],
      });
      notification.info(`Inbjudan skickad`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      return instance.delete(`${ENDPOINT}/${id}`);
    },
    onSuccess: () => {
      notification.info(`Inbjudan borttagen`);
      queryClient.invalidateQueries({
        queryKey: [GET_INVITATIONS_KEY],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useSignupByInvitation = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: SignupByInvitation): Promise<void> => {
      return instance.post(`${ENDPOINT}/signup`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CustomerQueryKey.GetOne],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export type SignupByInvitation = {
  password: string;
  name: string;
  code: string;
};
